import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useMenuItemNavigation from './useMenuItemNavigation';
import { resolveShopDestination } from '../services/shopDestination';
import { findMenuDestination, shopEntry, schoolShopPath } from '../services/menuDestination';

/**
 * Shared brand-Header glue for the two fixed header menu items.
 *
 * Owns the "menu intent" carried by an `open-school-selector` event
 * (`detail.menuItemKey` + `detail.fallbackTo`): while an intent is pending,
 * the school selector auto-picks the school's mapped shop (season) and the
 * Header navigates to it after the class step — or to the menu item's
 * fallback listing page when the school has no mapped shop. Dismissing the
 * popup just closes it (clear the intent via `clearIntent` in onClose).
 *
 * The intent lives in BOTH state (drives the memoized resolver prop) and a
 * ref: the DS SchoolSelector fires `onClose` ~500ms AFTER `onSelect` from a
 * closure captured at an earlier render, so select-path checks must read
 * through the ref.
 */
export default function useHeaderMenuIntent({ brandSlug, feSlug }) {
  const navigate = useNavigate();
  const onMenuItemClick = useMenuItemNavigation({ brandSlug, feSlug });

  const intentRef = useRef(null);
  const [menuIntent, setMenuIntent] = useState(null); // { menuItemKey, fallbackTo }

  const setIntent = useCallback((value) => {
    intentRef.current = value;
    setMenuIntent(value);
  }, []);

  /** Call from the open-school-selector event listener. */
  const readIntentFromEvent = useCallback((e) => {
    setIntent(e?.detail?.menuItemKey
      ? {
        menuItemKey: e.detail.menuItemKey,
        fallbackTo: e.detail.fallbackTo ?? null,
        // Landing clicks: resolve to the school's own shop / clicked item.
        toSchoolShop: Boolean(e.detail.toSchoolShop),
        target: e.detail.target ?? null,
        closedPath: e.detail.closedPath ?? null,
      }
      : null);
  }, [setIntent]);

  /**
   * Call when the selector is opened manually (header school button) and
   * when it's dismissed (onClose), so a stale intent can't leak into the
   * next open.
   */
  const clearIntent = useCallback(() => setIntent(null), [setIntent]);

  const menuKey = menuIntent?.menuItemKey ?? null;
  /** Passed to the SchoolSelector: auto-picks the mapped shop as the season. */
  const preferredSeasonResolver = useCallback(
    // Directly mapped shop first, else the shop carrying the mapped group.
    (school) => (menuKey ? findMenuDestination(school, menuKey).shop : null),
    [menuKey],
  );

  /**
   * Call first in onSelect. Returns true when a menu intent handled the
   * navigation (mapped shop URL, else the fallback page).
   */
  const navigateForIntentAfterSelect = useCallback((selection) => {
    const intent = intentRef.current;
    if (!intent) return false;
    setIntent(null);
    if (intent.toSchoolShop && selection?.school?.slug) {
      // Landing click: the shop the customer confirmed in the popup (preselected
      // to the section's shop) → the clicked item there, else the shop page;
      // no such shop → any open shop; none → the closed page.
      resolveShopDestination({
        brandSlug, feSlug, school: selection.school, menuKey: intent.menuItemKey,
        chosenShop: selection.season ?? null, className: selection.class ?? null,
        target: intent.target, closedPath: intent.closedPath ?? `/clothing/${feSlug}`,
      }).then((path) => navigate(path));
      return true;
    }
    // The shop the customer confirmed in the popup wins; inside it the group
    // mapped to the menu item, else the first group. No mapped shop → the
    // stored / first open shop (root is never the target when groups exist).
    const { shop, group } = shopEntry(selection?.school, intent.menuItemKey, selection?.season ?? null);
    if (shop?.slug && selection?.school?.slug) {
      navigate(schoolShopPath(feSlug, selection.school.slug, shop, group));
    } else if (intent.fallbackTo) {
      navigate(intent.fallbackTo);
    } else if (selection?.school?.slug) {
      // No fallback destination (open shop disabled): land on the school-shop
      // root — it shows the school's open shops or its own empty state, always
      // school-scoped. Re-opening the popup the customer just completed would
      // be a dead loop.
      navigate(`/clothing/${feSlug}/school-shop/${selection.school.slug}`);
    }
    return true;
  }, [navigate, brandSlug, feSlug, setIntent]);

  return {
    onMenuItemClick,
    hasMenuIntent: !!menuIntent,
    /** Landing intent: keep the shop dropdown visible (preselected), not hidden. */
    showPreferredSeason: Boolean(menuIntent?.toSchoolShop),
    readIntentFromEvent,
    clearIntent,
    preferredSeasonResolver,
    navigateForIntentAfterSelect,
  };
}
